import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Globe, Check } from "lucide-react";
import { Language, getDirection } from "../types/multilang";
import { FlagDisplay } from "./FlagDisplay";

interface LanguageSelectorProps {
  value: string;
  onChange: (code: string) => void;
  languages?: Language[];
  size?: "sm" | "md" | "lg";
  showFlag?: boolean;
  showLabel?: boolean;
  className?: string;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  value,
  onChange,
  languages,
  size = "md",
  showFlag = true,
  showLabel = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const safeLanguages = useMemo<Language[]>(() => (languages?.length ? languages : []), [languages]);
  const selectedLang = safeLanguages.find(l => l.code === value) || safeLanguages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (safeLanguages.length <= 1) return null;

  const sizeClasses = {
    sm: "px-2 py-1 text-xs gap-1",
    md: "px-3 py-1.5 text-sm gap-1.5",
    lg: "px-4 py-2 text-base gap-2",
  };
  const flagSize = size === "sm" ? 13 : size === "lg" ? 18 : 15;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center rounded-lg border border-borderGreyColor bg-white hover:border-primary/40 hover:bg-gray-50 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary/20 ${sizeClasses[size]}`}
      >
        <Globe className={`text-gray-400 ${size === "sm" ? "w-3 h-3" : "w-4 h-4"}`} />
        {showFlag && <FlagDisplay flag={selectedLang?.flag} size={flagSize} />}
        <span className="font-medium">{selectedLang?.code.toUpperCase()}</span>
        {showLabel && <span className="text-gray-400">({selectedLang?.label})</span>}
        <ChevronDown className={`transition-transform duration-150 text-gray-400 ${size === "sm" ? "w-3 h-3" : "w-4 h-4"} ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] bg-white rounded-xl border border-borderGreyColor shadow-lg overflow-hidden">
          <div className="py-1 max-h-[240px] overflow-y-auto w-[180px]">
            {safeLanguages.map(lang => (
              <button
                key={lang.code}
                type="button"
                onClick={() => { onChange(lang.code); setIsOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${value === lang.code ? "bg-primary/10" : ""}`}
              >
                <FlagDisplay flag={lang.flag} size={18} />
                <div className="flex-1 text-left">
                  <p className="font-medium text-iconTextGreyColor">{lang.label}</p>
                  <p className="text-xs text-gray-400">{lang.native_name}</p>
                </div>
                {getDirection(lang.code) === "rtl" && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-400">RTL</span>
                )}
                {value === lang.code && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};